import React from 'react';
import { Box, Typography } from '@mui/material';

const privacyPolicyStyles = {
    container: {
        padding: '20px',
        backgroundColor: '#f7f7f7',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        maxWidth: '800px',
        margin: '40px auto',
        lineHeight: '1.6',
        fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
        overflow: 'auto',
    },
    header: {
        marginBottom: '20px',
        borderBottom: '1px solid #e1e1e1',
        paddingBottom: '10px',
    },
    title: {
        fontSize: '24px',
        fontWeight: 'bold',
        color: '#333',
    },
    section: {
        marginBottom: '20px',
    },
    sectionTitle: {
        fontWeight: 'bold',
        color: '#333',
        marginBottom: '10px',
    },
    paragraph: {
        marginBottom: '10px',
        color: '#555',
    },
};

const PrivacyPolicy = () => {
    return (
        <Box style={privacyPolicyStyles.container}>
            <Box style={privacyPolicyStyles.header}>
                <Typography variant="h1" style={privacyPolicyStyles.title}>Privacy Policy</Typography>
                <Typography style={privacyPolicyStyles.paragraph}>Updated: August 13th, 2024</Typography>
                <Typography style={privacyPolicyStyles.paragraph}>
                    Connexxtion (“we,” “our,” or “us”) is committed to protecting your privacy. This Privacy Policy
                    explains how your personal information is collected, used, and disclosed by Connexxtion.
                    This Privacy Policy applies to our website and its associated subdomains (collectively, our
                    “Service”) as well as our application, Connexxtion. By accessing or using our Service, you
                    acknowledge that you have read, understood, and agree to our collection, storage, use, and
                    disclosure of your personal information as described in this Privacy Policy and our Terms of
                    Service.
                </Typography>
            </Box>
            <Box style={privacyPolicyStyles.section}>
                <Typography variant="h2" style={privacyPolicyStyles.sectionTitle}>Definitions and Key Terms</Typography>
                <Typography style={privacyPolicyStyles.paragraph}>
                    To clarify the terms used in this Privacy Policy, here is a list of definitions:
                </Typography>
                <ul style={{ color: '#555' }}>
                    <li>
                        <strong>Cookie:</strong> A small amount of data generated by a website and stored by your web
                        browser. It identifies your browser and can be used to remember your preferences or login
                        information.
                    </li>
                    <li>
                        <strong>Company:</strong> Refers to Connexxtion Social GmbH, located at Rossbergstrasse 15, 6410
                        Goldau, Switzerland, and responsible for your information under this Privacy Policy.
                    </li>
                    <li>
                        <strong>Country:</strong> The location where Connexxtion or its founders are based, which is
                        Switzerland.
                    </li>
                    <li>
                        <strong>Customer:</strong> Refers to any individual, company, or organization that registers to
                        use Connexxtion’s services.
                    </li>
                    <li>
                        <strong>Device:</strong> Any internet-connected device such as a phone, tablet, or computer used
                        to access Connexxtion’s services.
                    </li>
                    <li>
                        <strong>IP Address:</strong> A number assigned to your device by your Internet service provider,
                        which can often be used to identify your geographic location.
                    </li>
                    <li>
                        <strong>Personnel:</strong> Individuals employed by Connexxtion or under contract to perform
                        services on our behalf.
                    </li>
                    <li>
                        <strong>Personal Data:</strong> Any information that can identify an individual, either directly
                        or indirectly, including personal identification numbers.
                    </li>
                    <li>
                        <strong>Service:</strong> Refers to the services provided by Connexxtion as described in our
                        terms and on this platform.
                    </li>
                    <li>
                        <strong>Third-Party Service:</strong> Advertisers, contest sponsors, promotional and marketing
                        partners, and others who provide content or services we think may interest you.
                    </li>
                    <li>
                        <strong>Website:</strong> Connexxtion’s site, accessible at https://connexxtion.ch.
                    </li>
                    <li>
                        <strong>You:</strong> An individual or entity that registers with Connexxtion to use our
                        services.
                    </li>
                </ul>
            </Box>
            <Box style={privacyPolicyStyles.section}>
                <Typography variant="h2" style={privacyPolicyStyles.sectionTitle}>What Information Do We
                    Collect?</Typography>
                <Typography style={privacyPolicyStyles.paragraph}>
                    We collect information from you when you visit our website, register, place an order, subscribe to
                    our newsletter, respond to a survey, or fill out a form. This information may include:
                </Typography>
                <ul style={{ color: '#555' }}>
                    <li>
                        Name / Username
                    </li>
                    <li>
                        Phone Numbers
                    </li>
                    <li>
                        Email Addresses
                    </li>
                    <li>
                        Job Titles
                    </li>
                    <li>
                        Billing Addresses
                    </li>
                    <li>
                        Debit/Credit Card Numbers
                    </li>
                    <li>
                        Age
                    </li>
                    <li>
                        Password
                    </li>
                </ul>
            </Box>
            <Box style={privacyPolicyStyles.section}>
                <Typography variant="h2" style={privacyPolicyStyles.sectionTitle}>Use of End User Information from Third
                    Parties</Typography>
                <Typography style={privacyPolicyStyles.paragraph}>
                    Connexxtion collects End User Data necessary to provide our services. End users may voluntarily
                    provide us with information from social media websites. We may collect publicly available
                    information from these sites, but you can control what information is public by adjusting your
                    privacy settings on those platforms.
                </Typography>
            </Box>
            <Box style={privacyPolicyStyles.section}>
                <Typography variant="h2" style={privacyPolicyStyles.sectionTitle}>Use of Customer Information from Third
                    Parties</Typography>
                <Typography style={privacyPolicyStyles.paragraph}>
                    We may receive information from third parties when you contact us. For example, if you show interest
                    in becoming a Connexxtion customer, we may receive information from automated fraud detection
                    services. We also occasionally collect information made publicly available on social media websites.
                    You can control the amount of information shared publicly by adjusting your privacy settings on
                    these platforms.
                </Typography>
            </Box>
            <Box style={privacyPolicyStyles.section}>
                <Typography variant="h2" style={privacyPolicyStyles.sectionTitle}>Do We Share the Information We Collect
                    with Third Parties?</Typography>
                <Typography style={privacyPolicyStyles.paragraph}>
                    We may share your information, both personal and non-personal, with third parties such as
                    advertisers, promotional partners, and other entities whose products or services might interest you.
                    We may also share your information with our affiliated companies and business partners. In the event
                    of a merger, asset sale, or business reorganization, we may transfer your information to our
                    successors.
                    We may engage trusted third-party service providers to perform functions such as hosting, database
                    management, email services, credit card processing, and customer support. We will share your
                    personal information with these parties as necessary to provide their services.
                    We may share log file data, including IP addresses, with analytics partners and ad networks. This
                    data may be used to estimate location and other technical details. These third parties may aggregate
                    this information for auditing, research, and reporting.
                    We may also disclose personal and non-personal information to government authorities or law
                    enforcement if we believe it is necessary to respond to legal processes, protect our rights or those
                    of others, or comply with applicable laws.
                </Typography>
            </Box>
            <Box style={privacyPolicyStyles.section}>
                <Typography variant="h2" style={privacyPolicyStyles.sectionTitle}>Where and When Is Information
                    Collected from Customers and End Users?</Typography>
                <Typography style={privacyPolicyStyles.paragraph}>
                    Connexxtion collects personal information you submit directly to us, such as when you register on
                    our website, place an order, subscribe to our newsletter, or fill out a form. We may also receive
                    personal information about you from third parties, as detailed above.
                </Typography>
            </Box>
            <Box style={privacyPolicyStyles.section}>
                <Typography variant="h2" style={privacyPolicyStyles.sectionTitle}>How Do We Use the Information We
                    Collect?</Typography>
                <Typography style={privacyPolicyStyles.paragraph}>
                    We may use the information we collect from you for the following purposes:
                </Typography>
                <ul style={{ color: '#555' }}>
                    <li><strong>Personalization:</strong>To tailor your experience and better respond to your individual
                        needs.
                    </li>
                    <li><strong>Improvement:</strong>To enhance our website and services based on your feedback and
                        interactions.
                    </li>
                    <li><strong>Customer Service:</strong>To address your customer service requests and support needs
                        more effectively.
                    </li>
                    <li><strong>Transaction Processing:</strong>To process transactions and fulfill orders.
                    </li>
                    <li><strong>Administration:</strong>To manage contests, promotions, surveys, or other site features.
                    </li>
                    <li><strong>Communication:</strong>To send periodic emails regarding updates, promotions, or other
                        relevant information.
                    </li>
                </ul>
            </Box>
            <Box style={privacyPolicyStyles.section}>
                <Typography variant="h2" style={privacyPolicyStyles.sectionTitle}>How Do We Use Your Email
                    Address?</Typography>
                <Typography style={privacyPolicyStyles.paragraph}>
                    By providing your email address on our website, you consent to receive emails from us. You may
                    unsubscribe from these emails at any time by following the opt-out link included in each email. We
                    do not send unsolicited commercial emails and only contact individuals who have explicitly
                    authorized us to do so. We may use your email address for customer audience targeting on platforms
                    like Facebook, displaying custom ads to those who have opted in. Email addresses provided through
                    order processing are used solely for updates related to your order. If the same email is provided
                    through other methods, it may be used for any purpose outlined in this Privacy Policy. You can
                    unsubscribe from future emails using the instructions provided in each email.
                </Typography>
            </Box>
            <Box style={privacyPolicyStyles.section}>
                <Typography variant="h2" style={privacyPolicyStyles.sectionTitle}>How Long Do We Keep Your
                    Information?</Typography>
                <Typography style={privacyPolicyStyles.paragraph}>
                    We retain your information only for as long as necessary to provide our services and fulfill the
                    purposes outlined in this Privacy Policy. This applies to information shared with third parties who
                    perform services on our behalf. Once we no longer need your information and are not required to keep
                    it for legal or regulatory reasons, we will either delete it from our systems or anonymize it to
                    prevent identification.
                </Typography>
            </Box>
            <Box style={privacyPolicyStyles.section}>
                <Typography variant="h2" style={privacyPolicyStyles.sectionTitle}>How Do We Protect Your
                    Information?</Typography>
                <Typography style={privacyPolicyStyles.paragraph}>
                    We implement various security measures to protect your personal information when you place an order
                    or access your data. Our secure server uses Secure Socket Layer (SSL) technology to encrypt
                    sensitive information during transmission, which is then stored securely by our payment gateway
                    providers. Access to this information is restricted to authorized personnel with special access
                    rights. While we strive to ensure the security of your information, we cannot guarantee absolute
                    protection against unauthorized access, disclosure, alteration, or destruction.
                </Typography>
            </Box>
            <Box style={privacyPolicyStyles.section}>
                <Typography variant="h2" style={privacyPolicyStyles.sectionTitle}>Could My Information Be Transferred to
                    Other Countries?</Typography>
                <Typography style={privacyPolicyStyles.paragraph}>
                    As Connexxtion is based in Switzerland, your information may be transferred to and hosted in other
                    countries, including those with varying data protection laws. By using our services, you consent to
                    the trans-border transfer and hosting of your information to the extent permitted by applicable
                    laws.
                </Typography>
            </Box>
            <Box style={privacyPolicyStyles.section}>
                <Typography variant="h2" style={privacyPolicyStyles.sectionTitle}>Is the Information Collected Through
                    the Connexxtion Service Secure?</Typography>
                <Typography style={privacyPolicyStyles.paragraph}>
                    We take reasonable measures to protect your information through physical, electronic, and managerial
                    safeguards. Despite our efforts, no security system is completely foolproof. We cannot guarantee
                    absolute security of your information and will use reasonable efforts to protect it. If applicable
                    law requires a higher standard of protection, intentional misconduct will be the benchmark for our
                    compliance.
                </Typography>
            </Box>
            <Box style={privacyPolicyStyles.section}>
                <Typography variant="h2" style={privacyPolicyStyles.sectionTitle}>Can I Update or Correct My
                    Information?</Typography>
                <Typography style={privacyPolicyStyles.paragraph}>
                    The ability to update or correct your information depends on your relationship with Connexxtion.
                </Typography>
                <ul style={{ color: '#555' }}>
                    <li><strong>For Customers:</strong> You can request updates or corrections to your personally
                        identifiable information, change your communication preferences, or delete your information by
                        contacting us or canceling your account. Please note that these updates or deletions will not
                        affect information previously shared with third parties in accordance with this Privacy Policy.
                        To ensure privacy and security, we may verify your identity before making changes. Note that it
                        may not be possible to remove every record of your information due to backup and archival
                        processes. We will update, correct, or delete your information in our active databases and
                        readily searchable media as soon as reasonably and technically possible.
                    </li>
                    <li><strong>For End Users:</strong> If you wish to update, delete, or request information held about
                        you, please contact the organization that provides the Connexxtion services you use.
                    </li>
                    <li><strong>For Personnel:</strong> If you are a Connexxtion employee or applicant, you can request
                        to update, correct, or receive records of your information by contacting us. Changes will not
                        affect information shared with third parties before the update or deletion.
                    </li>
                </ul>
            </Box>
            <Box style={privacyPolicyStyles.section}>
                <Typography variant="h2" style={privacyPolicyStyles.sectionTitle}>Sale of Business</Typography>
                <Typography style={privacyPolicyStyles.paragraph}>
                    In the event of a sale, merger, or other transfer of substantially all of Connexxtion's assets, or
                    if we discontinue our business or enter bankruptcy proceedings, we may transfer your information to
                    a third party. The third party will be required to adhere to the terms of this Privacy Policy.
                </Typography>
            </Box>
            <Box style={privacyPolicyStyles.section}>
                <Typography variant="h2" style={privacyPolicyStyles.sectionTitle}>
                    Affiliates
                </Typography>
                <Typography style={privacyPolicyStyles.paragraph}>
                    We may share information, including personal information, with our Corporate Affiliates. "Corporate
                    Affiliates" are entities that control, are controlled by, or are under common control with
                    Connexxtion. Information shared with our Corporate Affiliates will be governed by the terms of this
                    Privacy Policy.
                </Typography>
            </Box>
            <Box style={privacyPolicyStyles.section}>
                <Typography variant="h2" style={privacyPolicyStyles.sectionTitle}>Governing Law</Typography>
                <Typography style={privacyPolicyStyles.paragraph}>
                    This Privacy Policy is governed by the laws of Switzerland, excluding its conflict of laws
                    provisions. Any disputes related to this Privacy Policy shall be subject to the exclusive
                    jurisdiction of the courts in Switzerland. Your use of our website and services may also be subject
                    to other local, state, national, or international laws.
                    By using Connexxtion or engaging with us, you agree to this Privacy Policy. If you do not agree, you
                    should discontinue use of our website and services. Continued use after changes to this Privacy
                    Policy, which do not significantly alter the use or disclosure of your personal information,
                    signifies your acceptance of the changes.
                </Typography>
            </Box>
            <Box style={privacyPolicyStyles.section}>
                <Typography variant="h2" style={privacyPolicyStyles.sectionTitle}>Your Consent</Typography>
                <Typography style={privacyPolicyStyles.paragraph}>
                    By using Connexxtion, registering an account, or making a purchase, you consent to our Privacy
                    Policy and agree to its terms. Our updated Privacy Policy ensures transparency regarding the
                    information collected and how it is used.
                </Typography>
            </Box>
            <Box style={privacyPolicyStyles.section}>
                <Typography variant="h2" style={privacyPolicyStyles.sectionTitle}>Links to Other Websites</Typography>
                <Typography style={privacyPolicyStyles.paragraph}>
                    Our Privacy Policy applies solely to the Connexxtion Services. Our website may contain links to
                    other websites that are not operated or controlled by Connexxtion. We are not responsible for the
                    content, accuracy, or opinions expressed on such external sites. When you navigate to another
                    website via a link on our platform, our Privacy Policy no longer applies. Your interactions with
                    those sites are governed by their own privacy policies. Third parties may use cookies or other
                    technologies to collect information about you.
                </Typography>
            </Box>
            <Box style={privacyPolicyStyles.section}>
                <Typography variant="h2" style={privacyPolicyStyles.sectionTitle}>Cookies</Typography>
                <Typography style={privacyPolicyStyles.paragraph}>
                    Connexxtion uses cookies to enhance the performance and functionality of our website. A cookie is a
                    small piece of data stored on your computer or mobile device by your web browser. While cookies are
                    not essential for our website to function, they improve your experience by remembering your login
                    details and preferences. You can disable cookies through your browser settings, but this may affect
                    your ability to use certain features of our site. We do not store personally identifiable
                    information in cookies.
                </Typography>
            </Box>
            <Box style={privacyPolicyStyles.section}>
                <Typography variant="h2" style={privacyPolicyStyles.sectionTitle}>Blocking and Disabling Cookies and
                    Similar Technologies</Typography>
                <Typography style={privacyPolicyStyles.paragraph}>
                    You can set your browser to block cookies and similar technologies. However, this may prevent
                    essential cookies from functioning properly and affect your ability to fully utilize our website.
                    Blocking cookies may also result in the loss of saved information, such as login details. To block
                    or delete cookies, consult your browser's help menu for specific instructions.
                </Typography>
            </Box>
            <Box style={privacyPolicyStyles.section}>
                <Typography variant="h2" style={privacyPolicyStyles.sectionTitle}>Payment Details</Typography>
                <Typography style={privacyPolicyStyles.paragraph}>
                    We are committed to securely storing any payment information you provide, including credit card
                    details. This information is protected using advanced security measures.
                </Typography>
            </Box>
            <Box style={privacyPolicyStyles.section}>
                <Typography variant="h2" style={privacyPolicyStyles.sectionTitle}>Kids' Privacy</Typography>
                <Typography style={privacyPolicyStyles.paragraph}>
                    We do not knowingly collect personal data from children under 13. If you are a parent or guardian
                    and discover that your child has provided us with personal data without your consent, please contact
                    us. We will take steps to remove such information from our servers if it is determined that it was
                    collected without parental consent.
                </Typography>
            </Box>
            <Box style={privacyPolicyStyles.section}>
                <Typography variant="h2" style={privacyPolicyStyles.sectionTitle}>Changes to Our Privacy
                    Policy</Typography>
                <Typography style={privacyPolicyStyles.paragraph}>
                    We may update our Privacy Policy to reflect changes in our services or legal requirements. We will
                    notify you of any significant changes through our Service, and you will have the opportunity to
                    review the updated Privacy Policy. Continued use of our Service after changes are made signifies
                    your acceptance of the new terms. If you do not agree to the updated Privacy Policy, you may delete
                    your account.
                </Typography>
            </Box>
            <Box style={privacyPolicyStyles.section}>
                <Typography variant="h2" style={privacyPolicyStyles.sectionTitle}>Third-Party Services</Typography>
                <Typography style={privacyPolicyStyles.paragraph}>
                    Our platform may include or link to third-party content and services ("Third-Party Services").
                    Connexxtion is not responsible for the accuracy, completeness, legality, or any other aspect of
                    Third-Party Services. Accessing and using these services is at your own risk and subject to the
                    terms and conditions of the respective third parties.
                </Typography>
            </Box>
            <Box style={privacyPolicyStyles.section}>
                <Typography variant="h2" style={privacyPolicyStyles.sectionTitle}>Tracking Technologies</Typography>
                <ul style={{ color: '#555' }}>
                    <li><strong>Cookies:</strong> We use cookies to enhance our platform's performance and
                        functionality, though they are not essential. Without cookies, you might need to re-enter login
                        details and some features, such as videos, may become unavailable.
                    </li>
                    <li><strong>Local Storage:</strong> Local Storage (DOM storage) offers a more extensive and
                        persistent data storage solution compared to cookies. It does not send data with HTTP requests
                        and supports larger data capacity.
                    </li>
                    <li><strong>Sessions:</strong> We use sessions to track the areas of our platform that you visit. A
                        session is temporary data stored on your device, helping to enhance your browsing experience.
                    </li>
                </ul>
            </Box>
            <Box style={privacyPolicyStyles.section}>
                <Typography variant="h2" style={privacyPolicyStyles.sectionTitle}>General Data Protection Regulation
                    (GDPR)
                    What is GDPR?</Typography>
                <Typography style={privacyPolicyStyles.paragraph}>
                    The General Data Protection Regulation (GDPR) is an EU-wide privacy and data protection law that
                    regulates how companies protect the personal data of EU residents and enhances individuals' control
                    over their personal data. GDPR applies globally, impacting any company handling EU residents' data,
                    not just those based in the EU.
                </Typography>
            </Box>
            <Box style={privacyPolicyStyles.section}>
                <Typography variant="h2" style={privacyPolicyStyles.sectionTitle}>What is Personal Data?</Typography>
                <Typography style={privacyPolicyStyles.paragraph}>
                    Personal data is any information that can identify an individual, either on its own or combined with
                    other data. Examples include names, email addresses, financial information, political opinions,
                    genetic and biometric data, IP addresses, physical addresses, sexual orientation, and ethnicity.
                </Typography>
            </Box>
            <Box style={privacyPolicyStyles.section}>
                <Typography variant="h2" style={privacyPolicyStyles.sectionTitle}>Data Protection
                    Principles</Typography>
                <Typography style={privacyPolicyStyles.paragraph}>
                    GDPR mandates the following principles:
                </Typography>
                <ul style={{ color: '#555' }}>
                    <li><strong>Fair and Lawful Processing:</strong> Personal data must be processed fairly, legally,
                        and transparently.
                    </li>
                    <li><strong>Purpose Limitation:</strong> Data should be collected for specific, legitimate purposes
                        and not used for other purposes.
                    </li>
                    <li><strong>Data Minimization:</strong> Only data necessary for the intended purpose should be
                        collected and retained.
                    </li>
                    <li><strong>Accuracy:</strong> Personal data should be kept accurate and up-to-date.
                    </li>
                    <li><strong>Storage Limitation:</strong> Data should be kept only as long as necessary for its
                        purpose.
                    </li>
                    <li><strong>Rights of Individuals:</strong> Individuals have the right to access, correct, delete,
                        or restrict their personal data.
                    </li>
                </ul>
            </Box>
            <Box style={privacyPolicyStyles.section}>
                <Typography variant="h2" style={privacyPolicyStyles.sectionTitle}>Why is GDPR Important?</Typography>
                <Typography style={privacyPolicyStyles.paragraph}>
                    GDPR introduces stringent requirements for data protection, enhancing privacy and imposing
                    substantial penalties for non-compliance. At Connexxtion, we prioritize data privacy and have
                    implemented robust security and privacy practices that exceed GDPR requirements.
                </Typography>
            </Box>
            <Box style={privacyPolicyStyles.section}>
                <Typography variant="h2" style={privacyPolicyStyles.sectionTitle}>Individual Data Subject's
                    Rights</Typography>
                <Typography style={privacyPolicyStyles.paragraph}>
                    Connexxtion ensures compliance with GDPR by using fully vetted, DPA-compliant vendors. We store
                    personal data for up to 6 years unless an account is deleted, in which case data is disposed of
                    within 60 days. You have the right to access, update, retrieve, or delete your data. If you need
                    assistance, our customer support team is available to help with API-related queries.
                </Typography>
            </Box>
            <Box style={privacyPolicyStyles.section}>
                <Typography variant="h2" style={privacyPolicyStyles.sectionTitle}>California Residents
                    California Consumer Privacy Act (CCPA)
                </Typography>
                <Typography style={privacyPolicyStyles.paragraph}>
                    Under CCPA, you have the following rights:
                </Typography>
                <ul style={{ color: '#555' }}>
                    <li><strong>Right to Know and Access:</strong> Request information on the categories and specific
                        pieces of Personal Information collected, the purposes for collection, and sources.
                    </li>
                    <li><strong>Right to Equal Service:</strong> We will not discriminate against you for exercising
                        your privacy rights.
                    </li>
                    <li><strong>Right to Delete:</strong> Request deletion of Personal Information associated with your
                        account.
                    </li>
                    <li><strong>Right to Opt-Out:</strong>Request that we do not sell your Personal Information.
                    </li>
                </ul>
                <Typography style={privacyPolicyStyles.paragraph}>
                    We do not sell Personal Information. Requests will be addressed within one month. For more
                    information or to exercise these rights, please contact us.
                </Typography>
            </Box>
            <Box style={privacyPolicyStyles.section}>
                <Typography variant="h2" style={privacyPolicyStyles.sectionTitle}>California Online Privacy Protection
                    Act (CalOPPA)
                </Typography>
                <Typography style={privacyPolicyStyles.paragraph}>
                    CalOPPA grants similar rights to CCPA:
                </Typography>
                <ul style={{ color: '#555' }}>
                    <li><strong>Right to Know and Access:</strong> Request information on the categories and specific
                        pieces of Personal Information collected, the purposes for collection, and sources.
                    </li>
                    <li><strong>Right to Equal Service:</strong> We will not discriminate against you for exercising
                        your privacy rights.
                    </li>
                    <li><strong>Right to Delete:</strong> Request deletion of Personal Information associated with your
                        account.
                    </li>
                    <li><strong>Right to Opt-Out:</strong>Request that we do not sell your Personal Information.
                    </li>
                </ul>
                <Typography style={privacyPolicyStyles.paragraph}>
                    We do not sell Personal Information. Requests will be addressed within one month. For more
                    information or to exercise these rights, please contact us.
                </Typography>
            </Box>
            <Box style={privacyPolicyStyles.section}>
                <Typography variant="h2" style={privacyPolicyStyles.sectionTitle}>Contact Us</Typography>
                <Typography style={privacyPolicyStyles.paragraph}>
                    For any questions regarding our privacy practices, please contact us:
                </Typography>
                <p style={privacyPolicyStyles.paragraph}>Email: <a
                    href="mailto:support@connexxtion.ch">support@connexxtion.ch</a></p>
            </Box>
        </Box>
    );
};

export default PrivacyPolicy;
